import { writeAuditLog } from '../components/common/apis';

const TRACKED_FIELDS = [
  ['agent_firstname', 'firstName'],
  ['agent_lastname', 'lastName'],
  ['agent_email', 'email'],
  ['agent_sms', 'phone'],
  ['isdefault', 'active'],
];

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const hasAlternateContactData = (agent) => {
  if (!agent) {
    return false;
  }
  const fields = [
    agent.agent_firstname,
    agent.agent_lastname,
    agent.agent_email,
    agent.agent_sms,
  ];
  return fields.some((value) => value != null && String(value).trim() !== '');
};

const isUuid = (value) => {
  const str = String(value || '').trim();
  return UUID_REGEX.test(str);
};

const normalizeFieldValue = (fieldKey, value) => {
  if (fieldKey === 'isdefault') {
    return Boolean(value);
  }
  return String(value || '').trim();
};

const getChangedFieldLabels = (current, original) => {
  if (!original) {
    return [];
  }
  return TRACKED_FIELDS.map(([fieldKey, label]) => label).filter(
    (label, index) => {
      const fieldKey = TRACKED_FIELDS[index][0];
      return (
        normalizeFieldValue(fieldKey, current[fieldKey]) !==
        normalizeFieldValue(fieldKey, original[fieldKey])
      );
    }
  );
};

export const snapshotAlternetAgents = (agents) =>
  JSON.parse(JSON.stringify(agents || []));

/**
 * Portal user's display name for audit descriptions (never a Cognito/sub UUID).
 */
export const getAuditActorDisplayName = () => {
  try {
    const firstName = sessionStorage.getItem('user.firstname');
    const lastName = sessionStorage.getItem('user.lastname');
    const fullName = [firstName, lastName]
      .filter((part) => part != null && String(part).trim() !== '')
      .join(' ')
      .trim();
    if (fullName) {
      return fullName;
    }

    const user = JSON.parse(sessionStorage.getItem('user'));
    if (!user) {
      return 'user';
    }

    const payload =
      user.signInUserSession &&
      user.signInUserSession.idToken &&
      user.signInUserSession.idToken.payload;

    if (payload && payload.name && !isUuid(payload.name)) {
      return String(payload.name).trim();
    }

    if (payload && payload.email && !isUuid(payload.email)) {
      return String(payload.email).trim();
    }

    const userFirstLast = [user.first_name, user.last_name]
      .filter((part) => part != null && String(part).trim() !== '')
      .join(' ')
      .trim();
    if (userFirstLast) {
      return userFirstLast;
    }

    if (user.username && !isUuid(user.username)) {
      return String(user.username).trim();
    }

    return 'user';
  } catch (_err) {
    return 'user';
  }
};

/** @deprecated Use getAuditActorDisplayName */
export const getAuditActor = getAuditActorDisplayName;

/**
 * Returns alternate contacts that are newly added (not yet persisted).
 */
export const getNewAlternateContacts = (agents, { isCreate = false } = {}) => {
  return (agents || []).filter((agent) => {
    if (!hasAlternateContactData(agent)) {
      return false;
    }
    if (isCreate) {
      return true;
    }
    return !agent.id;
  });
};

/**
 * Compares current vs original alternate contacts (no PHI in result).
 */
export const getAlternateContactChanges = (
  current,
  original,
  { isCreate = false } = {}
) => {
  const added = getNewAlternateContacts(current, { isCreate });
  const changedFieldSummary = new Set();

  if (isCreate) {
    return {
      added,
      updatedCount: 0,
      removedCount: 0,
      changedFieldSummary: [],
    };
  }

  const originalById = {};
  (original || []).forEach((agent) => {
    if (agent && agent.id) {
      originalById[agent.id] = agent;
    }
  });

  const currentIds = new Set();
  let updatedCount = 0;

  (current || []).forEach((agent) => {
    if (!agent || !agent.id || !hasAlternateContactData(agent)) {
      return;
    }
    currentIds.add(agent.id);
    const previous = originalById[agent.id];
    if (!previous) {
      return;
    }
    const fields = getChangedFieldLabels(agent, previous);
    if (fields.length > 0) {
      updatedCount += 1;
      fields.forEach((field) => changedFieldSummary.add(field));
    }
  });

  const removedCount = (original || []).filter(
    (agent) => agent && agent.id && !currentIds.has(agent.id)
  ).length;

  return {
    added,
    updatedCount,
    removedCount,
    changedFieldSummary: Array.from(changedFieldSummary),
  };
};

const buildAuditMetadata = (changeType, extra = {}) => {
  const actor = getAuditActorDisplayName();
  return {
    actor,
    changeType,
    ...extra,
  };
};

const persistAudit = async (description, actionName, auditContext) => {
  try {
    await writeAuditLog(
      description,
      actionName,
      {
        parentAccountId: sessionStorage.getItem('parentAccountId'),
      },
      auditContext,
      { wait: false }
    );
  } catch (error) {
    console.error('Alternate contact audit log failed:', error);
  }
};

/**
 * Logged when user edits alternate contact fields (once per dialog session).
 */
export const logAlternateContactEditAudit = async () => {
  const actorLabel = getAuditActorDisplayName();
  const description = `Alternate contact details edited by ${actorLabel}.`;

  await persistAudit(description, 'Manage Patients - Edit', {
    eventType: 'ALTERNATE_CONTACT_EDITED',
    source: 'PORTAL',
    severity: 'INFO',
    metadata: buildAuditMetadata('edit'),
  });
};

/**
 * Logged when user removes an alternate contact row (persisted contacts only).
 */
export const logAlternateContactRemoveAudit = async (removedCount = 1) => {
  const count = removedCount || 1;
  const actorLabel = getAuditActorDisplayName();
  const description =
    count === 1
      ? `Alternate contact removed by ${actorLabel}.`
      : `${count} alternate contacts removed by ${actorLabel}.`;

  await persistAudit(description, 'Manage Patients - Remove', {
    eventType: 'ALTERNATE_CONTACT_REMOVED',
    source: 'PORTAL',
    severity: 'INFO',
    metadata: buildAuditMetadata('remove', { removedCount: count }),
  });
};

/**
 * Logged after alternate contacts are successfully saved (separate entry per change type).
 */
export const logAlternateContactSaveAudit = async (changes) => {
  if (!changes) {
    return;
  }

  const addedCount = (changes.added && changes.added.length) || 0;
  const { updatedCount, removedCount, changedFieldSummary } = changes;

  if (addedCount === 0 && updatedCount === 0 && removedCount === 0) {
    return;
  }

  const actorLabel = getAuditActorDisplayName();

  if (addedCount > 0) {
    const description =
      addedCount === 1
        ? `Alternate contact added by ${actorLabel}.`
        : `${addedCount} alternate contacts added by ${actorLabel}.`;

    await persistAudit(description, 'Manage Patients - Save', {
      eventType: 'ALTERNATE_CONTACT_ADDED',
      source: 'PORTAL',
      severity: 'INFO',
      metadata: buildAuditMetadata('save', { addedCount }),
    });
  }

  if (updatedCount > 0) {
    const description =
      updatedCount === 1
        ? `Alternate contact updated by ${actorLabel}.`
        : `${updatedCount} alternate contacts updated by ${actorLabel}.`;

    await persistAudit(description, 'Manage Patients - Save', {
      eventType: 'ALTERNATE_CONTACT_UPDATED',
      source: 'PORTAL',
      severity: 'INFO',
      metadata: buildAuditMetadata('save', {
        updatedCount,
        fieldsChanged: changedFieldSummary || [],
      }),
    });
  }

  if (removedCount > 0) {
    await logAlternateContactRemoveAudit(removedCount);
  }
};



// WEBPACK FOOTER //
// ./src/utils/alternateContactAudit.js